import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import apiClient from '../lib/apiClient';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const [cvLinks, setCvLinks] = useState([]);
  const [loadingCV, setLoadingCV] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Projects', path: '/projects' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  // Fetch CV links when modal opens
  useEffect(() => {
    if (!cvModalOpen || cvLinks.length > 0) {
      return;
    }

    const loadCVLinks = async () => {
      try {
        setLoadingCV(true);
        const data = await apiClient.get('/api/cv-links');
        // Convert object to array if needed
        const linksArray = Array.isArray(data)
          ? data
          : Object.entries(data).map(([role, url]) => ({ role, url }));
        setCvLinks(linksArray);
      } catch (error) {
        console.error('Failed to load CV links:', error);
      } finally {
        setLoadingCV(false);
      }
    };

    loadCVLinks();
  }, [cvModalOpen, cvLinks]);

  const openCvModal = () => {
    setMobileMenuOpen(false);
    setCvModalOpen(true);
  };

  const closeCvModal = () => setCvModalOpen(false);

  return (
    <>
      <header className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-24px)] max-w-5xl px-4 md:px-6">
        <nav className="bg-white/60 text-neutral-900 backdrop-blur-md rounded-full px-4 md:px-6 py-3 shadow-lg border border-neutral-200">
          <div className="flex items-center justify-between">
            {/* Logo/Name */}
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              aria-label="Go to home"
            >
              <img
                src="/portfolio_icon.png"
                alt="Portfolio"
                className="w-9 h-9 rounded-full object-cover"
              />
              <span className="hidden sm:inline text-sm font-semibold text-neutral-900">Sushant Kumar Yadav</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors relative ${
                    isActive(item.path)
                      ? 'text-neutral-900 after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-0.5 after:bg-neutral-900'
                      : 'text-neutral-700 hover:text-neutral-900'
                  }`}
                >
                  {item.name}
                </Link>
              ))}

              <button
                type="button"
                onClick={openCvModal}
                className="px-4 py-2 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700 hover:shadow-md transform hover:-translate-y-0.5 transition-all"
              >
                Download CV
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-neutral-900 hover:text-neutral-500 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-white/80 md:hidden">
          <div className="flex flex-col items-center justify-center min-h-screen px-6 py-20">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`text-4xl font-medium mb-8 transition-colors ${
                  isActive(item.path)
                    ? 'text-neutral-900'
                    : 'text-neutral-500 hover:text-neutral-900'
                }`}
              >
                {item.name}
              </Link>
            ))}

            <button
              type="button"
              onClick={openCvModal}
              className="mt-6 px-6 py-3 rounded-full bg-neutral-900 text-white text-base font-medium hover:bg-neutral-700 hover:shadow-lg transform hover:scale-105 transition-all"
            >
              Download CV
            </button>
          </div>
        </div>
      )}

      {/* CV Modal */}
      {cvModalOpen && (
        <div className="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={closeCvModal} aria-hidden="true" />
          <div className="relative z-10 mx-auto mt-32 w-[calc(100%-32px)] max-w-xl rounded-2xl border border-white/60 bg-white/80 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-neutral-900">Select CV by Role</h2>
              <button
                type="button"
                onClick={closeCvModal}
                className="rounded-full p-2 text-neutral-700 hover:bg-white/70 hover:text-neutral-900 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-neutral-600 mb-6">Choose the role to view or download the CV.</p>
            {loadingCV ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900" />
              </div>
            ) : cvLinks.length === 0 ? (
              <p className="text-center text-neutral-600 py-8">No CV links available at the moment.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {cvLinks.map((link) => (
                  <a
                    key={link.role || link.label}
                    href={link.url || link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between rounded-xl border border-white/70 bg-white/90 px-4 py-3 text-neutral-900 transition-all hover:bg-white hover:shadow"
                  >
                    <span className="font-medium">{link.role || link.label}</span>
                    <span className="text-sm text-neutral-500">Open →</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
